export class DateSelectionComponent {
    private earliestDate: Date;
    private latestDate: Date;
    private startDateElement: HTMLElement;
    private latestDateElement: HTMLElement;
    private dateRangeElement: HTMLInputElement;
    private currentDate: Date;
    private debounceTimeout: number | null = null;

    constructor(
        private rootElement: HTMLElement,
        private selectedCoins: Set<any>,
        private coinIdToCoinMap: Map<string, any>,
        private changeDateCb: (date: string | null) => void
    ) {
        this.startDateElement = this.rootElement.querySelector('.earliest-date')!;
        this.latestDateElement = this.rootElement.querySelector('.latest-date')!;
        this.dateRangeElement = this.rootElement.querySelector('.date-range')!;
        this.earliestDate = new Date();
        this.latestDate = new Date();
        this.currentDate = new Date();
        this.init();
        this.update();
    }

    private debounce(callback: () => void, delay: number) {
        if (this.debounceTimeout) {
            window.clearTimeout(this.debounceTimeout);
        }
        this.debounceTimeout = window.setTimeout(callback, delay);
    }

    init() {
        this.dateRangeElement.addEventListener('input', () => {
            const percentage = parseInt(this.dateRangeElement.value);
            this.currentDate = this.getDateFromPercentage(percentage);
            this.startDateElement.textContent = this.formatDate(this.currentDate);
            
            // Debounce the callback to prevent too many updates
            this.debounce(() => {
                // If at earliest date, pass null to remove the parameter
                if (this.currentDate.getTime() === this.earliestDate.getTime()) {
                    this.changeDateCb(null);
                } else {
                    this.changeDateCb(this.currentDate.toISOString().split('T')[0]);
                }
            }, 100);
        });
    }

    setDate(dateStr: string) {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return;

        // Calculate the percentage for the range input
        const totalTime = this.latestDate.getTime() - this.earliestDate.getTime();
        const time = date.getTime() - this.earliestDate.getTime();
        const percentage = Math.round((time / totalTime) * 100);
        
        // Update the range input and display
        this.dateRangeElement.value = percentage.toString();
        this.currentDate = date;
        this.startDateElement.textContent = this.formatDate(date);
    }

    reset() {
        this.currentDate = this.earliestDate;
        this.dateRangeElement.value = '0';
        this.startDateElement.textContent = this.formatDate(this.earliestDate);
        this.changeDateCb(null); // Notify that we're at the earliest date
    }

    private getDateFromPercentage(percentage: number): Date {
        const totalTime = this.latestDate.getTime() - this.earliestDate.getTime();
        const time = (percentage / 100) * totalTime + this.earliestDate.getTime();
        return new Date(time);
    }

    private formatDate(date: Date): string {
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    update() {
        const coins = Array.from(this.selectedCoins).map(coinId => this.coinIdToCoinMap.get(coinId));
        if (coins.length === 0) return;

        // Find earliest and latest dates
        this.earliestDate = coins
            .map(coin => new Date(coin.startDate))
            .reduce((a, b) => a < b ? a : b);
        
        this.latestDate = new Date(); // Current date as the latest date
        this.currentDate = this.earliestDate; // Reset current date to earliest date

        // Update the display
        this.startDateElement.textContent = this.formatDate(this.earliestDate);
        this.latestDateElement.textContent = this.formatDate(this.latestDate);
        
        // Reset the range input to 0
        this.dateRangeElement.value = '0';
    }
}
